/**
 * useLoginProfiles — 로그인 프로파일 관리 composable
 *
 * DB 클라이언트 UX: Mobius 주소 + ID + Password를 프로파일로 관리, localStorage 저장
 */

import { ref } from 'vue'

const STORAGE_KEY = 'dronecast.loginProfiles'

export interface LoginProfile {
  id: string
  name: string         // 프로파일 이름 (예: "운영 서버")
  mobiusUrl: string    // Mobius 서버 주소
  email: string        // 사용자 이메일
  savePassword: boolean
  password: string     // savePassword가 true일 때만 저장
  lastLoginAt?: number
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
}

function loadProfiles(): LoginProfile[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveProfiles(profiles: LoginProfile[]) {
  // 비밀번호 저장 비활성화된 프로파일은 password 제거 후 저장
  const toSave = profiles.map(p => ({
    ...p,
    password: p.savePassword ? p.password : '',
  }))
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
}

export function useLoginProfiles() {
  const profiles = ref<LoginProfile[]>(loadProfiles())
  const selectedId = ref<string | null>(profiles.value[0]?.id || null)

  function addProfile(profile?: Partial<LoginProfile>): LoginProfile {
    const newProfile: LoginProfile = {
      id: generateId(),
      name: profile?.name || '새 프로파일',
      mobiusUrl: profile?.mobiusUrl || 'http://gcs.iotocean.org:7579/Mobius',
      email: profile?.email || '',
      savePassword: false,
      password: '',
    }
    profiles.value.push(newProfile)
    selectedId.value = newProfile.id
    saveProfiles(profiles.value)
    return newProfile
  }

  function updateProfile(id: string, updates: Partial<LoginProfile>) {
    const idx = profiles.value.findIndex(p => p.id === id)
    if (idx >= 0) {
      profiles.value[idx] = { ...profiles.value[idx], ...updates }
      saveProfiles(profiles.value)
    }
  }

  function removeProfile(id: string) {
    profiles.value = profiles.value.filter(p => p.id !== id)
    if (selectedId.value === id) {
      selectedId.value = profiles.value[0]?.id || null
    }
    saveProfiles(profiles.value)
  }

  function markLoginSuccess(id: string) {
    updateProfile(id, { lastLoginAt: Date.now() })
  }

  function getSelected(): LoginProfile | undefined {
    return profiles.value.find(p => p.id === selectedId.value)
  }

  // 프로파일이 없으면 기본 프로파일 생성
  if (profiles.value.length === 0) {
    addProfile({
      name: '기본 서버',
      mobiusUrl: 'http://gcs.iotocean.org:7579/Mobius',
    })
  }

  return {
    profiles,
    selectedId,
    addProfile,
    updateProfile,
    removeProfile,
    markLoginSuccess,
    getSelected,
  }
}
